import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import { RoleProvider } from './context/RoleContext';
import { DataProvider } from './context/DataContext';
import { ThemeProvider } from './context/ThemeContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import PriceList from './pages/PriceList';
import NewDocument from './pages/NewDocument';
import Documents from './pages/Documents';
import ImportProducts from './pages/ImportProducts';
import ImportCustomers from './pages/ImportCustomers';
import ViewDocument from './pages/ViewDocument';
import EditDocument from './pages/EditDocument';
import CustomerProfile from './pages/CustomerProfile';
import Estimator from './pages/Estimator';
import EstimatorResults from './pages/EstimatorResults';
import VinylEstimator from './pages/VinylEstimator';
import WoodEstimator from './pages/WoodEstimator';
import AluminumEstimator from './pages/AluminumEstimator';
import Settings from './pages/Settings';
import Inventory from './pages/Inventory';
import QuickPriceLookup from './pages/QuickPriceLookup';
import SpecialOrderTemplates from './pages/SpecialOrderTemplates';
import Reports from './pages/Reports';
import NailCalculator from './pages/NailCalculator';
import Announcements from './pages/Announcements';
import ShopDrawings from './pages/ShopDrawings';
import Shop from './pages/Shop';
import { useRole } from './hooks/useRole';

/**
 * Routing for a signed-in user.
 *
 * ⚠️ UI-LEVEL GUARD, NOT SECURITY.
 *
 * This decides which React component renders for a URL. It does NOT stop
 * anyone reaching the data: the repo carries no Firestore rules at all
 * (firebase.json configures hosting and nothing else), so any authenticated
 * session can read and write every collection straight from a console or a
 * fetch. A determined person walks around this in a minute.
 *
 * What it does do is stop a welder on a workshop tablet landing in pricing by
 * typing a URL or restoring a tab. Real enforcement arrives with the rules, and
 * this guard is not a substitute for writing them.
 *
 * It lives in its own component because useRole() has to be called INSIDE the
 * RoleProvider, and App renders the provider.
 */
function AuthedRoutes({ user }) {
  const { isShop } = useRole();

  // A shop user gets one screen. Every other path — including unknown ones —
  // redirects to it, so a bookmarked or typed URL cannot land anywhere else.
  if (isShop) {
    return (
      <Routes>
        <Route path="/shop" element={<Shop />} />
        <Route path="*" element={<Navigate to="/shop" replace />} />
      </Routes>
    );
  }

  // Everyone else: exactly the routing that was here before, plus /shop.
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" />} />
      <Route path="/dashboard" element={<Dashboard user={user} />} />
      <Route path="/customers" element={<Customers user={user} />} />
      <Route path="/pricelist" element={<PriceList user={user} />} />
      <Route path="/quick-prices" element={<QuickPriceLookup user={user} />} />
      <Route path="/templates" element={<SpecialOrderTemplates user={user} />} />
      <Route path="/new-quote" element={<NewDocument user={user} docType="quote" />} />
      <Route path="/new-invoice" element={<NewDocument user={user} docType="invoice" />} />
      <Route path="/documents" element={<Documents user={user} />} />
      <Route path="/view-document/:type/:id" element={<ViewDocument user={user} />} />
      <Route path="/edit-document/:type/:id" element={<EditDocument user={user} />} />
      <Route path="/customer-profile/:id" element={<CustomerProfile user={user} />} />
      <Route path="/estimator" element={<Estimator user={user} />} />
      <Route path="/vinyl-estimator" element={<VinylEstimator user={user} />} />
      <Route path="/wood-estimator" element={<WoodEstimator user={user} />} />
      <Route path="/aluminum-estimator" element={<AluminumEstimator user={user} />} />
      <Route path="/estimator-results" element={<EstimatorResults user={user} />} />
      <Route path="/import-products" element={<ImportProducts user={user} />} />
      <Route path="/import-customers" element={<ImportCustomers user={user} />} />
      <Route path="/settings" element={<Settings user={user} />} />
      <Route path="/inventory" element={<Inventory user={user} />} />
      <Route path="/reports" element={<Reports user={user} />} />
      <Route path="/nail-calculator" element={<NailCalculator user={user} />} />
      {/* The page hides itself from anyone but the owner — see the
          banner comment in Announcements.js on why that is a UI hide
          and not security. */}
      <Route path="/announcements" element={<Announcements user={user} />} />
      <Route path="/shop-drawings" element={<ShopDrawings user={user} />} />
      {/* Reachable by an admin too, for checking what the shop sees. */}
      <Route path="/shop" element={<Shop />} />
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // Prevent the mouse wheel from changing <input type="number"> values app-wide.
  // Sibling to the spinner-arrow removal in index.css: both neutralize the
  // browser's built-in number-input increment behavior. Gated on
  // document.activeElement so normal page/modal scrolling is unaffected —
  // it only fires when the wheel is over the number input that currently has focus.
  useEffect(() => {
    const handleWheel = (e) => {
      const el = document.activeElement;
      if (el && el.tagName === 'INPUT' && el.type === 'number' && el === e.target) {
        e.preventDefault();
      }
    };
    document.addEventListener('wheel', handleWheel, { passive: false });
    return () => document.removeEventListener('wheel', handleWheel);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-blue-900 flex items-center justify-center">
        <p className="text-white text-xl">Loading...</p>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <ThemeProvider>
      <RoleProvider>
        {user ? (
          <DataProvider>
            <AuthedRoutes user={user} />
          </DataProvider>
        ) : (
          <Routes>
            <Route path="*" element={<Login />} />
          </Routes>
        )}
      </RoleProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
